/**
 * Canonical internal-link registry.
 *
 * Every internal link to one of these pages, related-links blocks,
 * breadcrumbs, the footer, structured data, reads its label from here, so a
 * page is described with the same anchor text everywhere on the site. Change a
 * label once and it changes everywhere.
 */

import { SITE } from "./site-content";

export type LinkPath =
  | "/"
  | "/what-i-do"
  | "/board-advisory"
  | "/who-i-am"
  | "/happy-clients"
  | "/case-studies"
  | "/contact"
  | "/service-finder"
  | "/accessibility"
  | "/fractional-cto-uk"
  | "/fractional-cto-us"
  | "/fractional-cto-southeast-asia"
  | "/fractional-cto-manchester"
  | "/fractional-cto-hong-kong"
  | "/fractional-cto-kuala-lumpur"
  | "/fractional-cto-malaysia"
  | "/technology-non-executive-director-uk"
  | "/technical-due-diligence-investors-uk"
  | "/technology-due-diligence-ma"
  | "/fractional-cto-vs-full-time-vs-agency"
  | "/fractional-cto-cost"
  | "/interim-cto-vs-fractional-cto"
  | "/how-to-hire-a-fractional-cto"
  | "/part-time-cto"
  | "/fractional-cto-digital-health"
  | "/when-to-hire-your-first-cto"
  | "/what-a-technology-non-executive-director-does"
  | "/non-executive-director-vs-advisor-vs-chair"
  | "/ai-governance-for-leadership-teams"
  | "/questions"
  | "/technology-decision-risk-check";

export type LinkEntry = {
  /** Canonical anchor text. Used for every link to this page. */
  anchor: string;
  /** Shorter label for breadcrumb trails, where the full anchor would wrap. */
  crumb?: string;
  /** One line explaining what the page covers, for card-style related links. */
  descriptor: string;
  /** Parent page in the site hierarchy; drives breadcrumbs. */
  parent?: LinkPath;
};

export const PAGES: Record<LinkPath, LinkEntry> = {
  "/accessibility": {
    anchor: "Accessibility",
    descriptor: "Our accessibility target, testing approach and how to report a barrier.",
    parent: "/",
  },
  "/service-finder": {
    anchor: "Find the right Scryla service",
    crumb: "Service finder",
    descriptor: "A suggested service and next step based on your company's technical challenge.",
    parent: "/contact",
  },
  "/technology-decision-risk-check": {
    anchor: "Technology decision risk check",
    crumb: "Risk check",
    descriptor:
      "Twelve questions on delivery, suppliers, cost, resilience and board visibility, with the read on the page.",
    parent: "/",
  },
  "/": {
    anchor: "Home",
    descriptor: "Technology leadership for founders and boards.",
  },
  "/what-i-do": {
    anchor: "Fractional CTO",
    descriptor: "How fractional CTO and advisory engagements are structured.",
    parent: "/",
  },
  "/fractional-cto-digital-health": {
    anchor: "Fractional CTO for digital health",
    crumb: "Digital health",
    descriptor:
      "Senior part-time technology leadership for UK digital health, covering product, delivery, security, DSPT and suppliers.",
    parent: "/what-i-do",
  },
  "/part-time-cto": {
    anchor: "Part-time CTO",
    descriptor:
      "The part-time CTO arrangement: what it covers, how many days it takes and what it costs.",
    parent: "/what-i-do",
  },
  "/board-advisory": {
    anchor: "Board and advisory",
    descriptor: "Non-executive and advisory work, kept separate from delivery engagements.",
    parent: "/",
  },
  "/who-i-am": {
    anchor: "Who I am",
    descriptor: "Twenty years as a CTO and COO. Founded Scryla in 2014.",
    parent: "/",
  },
  "/happy-clients": {
    anchor: "Client feedback",
    descriptor: "What founders, boards and colleagues have said about working together.",
    parent: "/",
  },
  "/case-studies": {
    anchor: "Case studies",
    descriptor: "Named engagements with the outcomes they produced.",
    parent: "/",
  },
  "/contact": {
    anchor: "Start a conversation",
    descriptor: "Tell me about the business and what is worrying you.",
    parent: "/",
  },

  // Regional hubs.
  "/fractional-cto-uk": {
    anchor: "Fractional CTO in the UK",
    crumb: "Fractional CTO in the UK",
    descriptor: "Remote-first across the UK, with London and Manchester on-site days available.",
    parent: "/what-i-do",
  },
  "/fractional-cto-us": {
    anchor: "Fractional CTO in the US",
    crumb: "Fractional CTO in the US",
    descriptor: "Remote engagements with US founders, covering East and West coast hours.",
    parent: "/what-i-do",
  },
  "/fractional-cto-southeast-asia": {
    anchor: "Fractional CTO in South East Asia",
    crumb: "Fractional CTO in South East Asia",
    descriptor: "GMT+8 coverage for scale-ups across Singapore, Malaysia and Hong Kong.",
    parent: "/what-i-do",
  },

  // Cities, each under its regional hub.
  "/fractional-cto-manchester": {
    anchor: "Fractional CTO in Manchester",
    descriptor: "On-site days across Greater Manchester and the North West corridor.",
    parent: "/fractional-cto-uk",
  },
  "/fractional-cto-malaysia": {
    anchor: "Fractional CTO in Malaysia",
    descriptor: "Technology leadership for Malaysian founders and boards.",
    parent: "/fractional-cto-southeast-asia",
  },
  "/fractional-cto-kuala-lumpur": {
    anchor: "Fractional CTO in Kuala Lumpur",
    descriptor: "Delivery assurance and hiring support for KL-based technology teams.",
    parent: "/fractional-cto-malaysia",
  },
  "/fractional-cto-hong-kong": {
    anchor: "Fractional CTO in Hong Kong",
    descriptor: "Senior technical judgement for Hong Kong founders, in local hours.",
    parent: "/fractional-cto-southeast-asia",
  },

  // Board and advisory.
  "/technology-non-executive-director-uk": {
    anchor: "Technology non-executive director in the UK",
    crumb: "Technology NED in the UK",
    descriptor: "Board-level technology scrutiny for UK companies and their investors.",
    parent: "/board-advisory",
  },
  "/technical-due-diligence-investors-uk": {
    anchor: "Technical due diligence for investors",
    crumb: "Technical due diligence",
    descriptor:
      "Independent technology review or detailed code audit for VC, private equity and acquirers, with every finding verified.",
    parent: "/board-advisory",
  },
  "/technology-due-diligence-ma": {
    anchor: "Technology due diligence for M&A",
    crumb: "Technology due diligence for M&A",
    descriptor:
      "Buy-side assessment, sell-side exit readiness, vendor technical due diligence and first-100-day technology planning.",
    parent: "/technical-due-diligence-investors-uk",
  },

  // Guides.
  "/fractional-cto-vs-full-time-vs-agency": {
    anchor: "Fractional CTO vs full-time vs agency",
    descriptor: "What each model actually costs you, and where each one fails.",
    parent: "/what-i-do",
  },
  "/fractional-cto-cost": {
    anchor: "What a fractional CTO costs",
    crumb: "Fractional CTO cost",
    descriptor: "Published UK salary and day rate benchmarks, and how the cost shapes compare.",
    parent: "/what-i-do",
  },
  "/how-to-hire-a-fractional-cto": {
    anchor: "How to hire a fractional CTO",
    crumb: "Hiring a fractional CTO",
    descriptor:
      "What to ask, what to check and what the engagement letter should say before you appoint one.",
    parent: "/what-i-do",
  },
  "/interim-cto-vs-fractional-cto": {
    anchor: "Interim CTO vs fractional CTO",
    descriptor: "Two different jobs that get the same job title.",
    parent: "/what-i-do",
  },
  "/when-to-hire-your-first-cto": {
    anchor: "When to hire your first CTO",
    descriptor: "The signals that say you need one, and the ones that say you do not yet.",
    parent: "/what-i-do",
  },
  "/what-a-technology-non-executive-director-does": {
    anchor: "What a technology NED actually does",
    descriptor: "The duties, the time commitment and the liability, under UK law.",
    parent: "/board-advisory",
  },
  "/ai-governance-for-leadership-teams": {
    anchor: "AI governance for leadership teams",
    crumb: "AI governance",
    descriptor:
      "Where AI belongs in an operating model, what it is allowed to do, and who is accountable when it is wrong.",
    parent: "/board-advisory",
  },
  "/non-executive-director-vs-advisor-vs-chair": {
    anchor: "NED vs advisor vs chair",
    descriptor: "Which board role fits the problem you are trying to solve.",
    parent: "/board-advisory",
  },

  // Question and answer library. Short, sourced answers to single queries.
  "/questions": {
    anchor: "Questions and answers",
    crumb: "Questions",
    descriptor: "Short, sourced answers on board roles and fractional CTO work.",
    parent: "/",
  },
};

/** Canonical anchor text for a page. */
export function anchorFor(path: LinkPath): string {
  return PAGES[path].anchor;
}

/** One-line descriptor for a page. */
export function descriptorFor(path: LinkPath): string {
  return PAGES[path].descriptor;
}

export type Crumb = { path: LinkPath; label: string };

/**
 * Walks the parent chain to build a breadcrumb trail, root first, current
 * page last. Returns an empty array for the home page, which needs no trail.
 */
export function breadcrumbTrail(path: LinkPath): Crumb[] {
  if (path === "/") return [];
  const trail: Crumb[] = [];
  let current: LinkPath | undefined = path;
  // Guard against a malformed cycle in the registry.
  let depth = 0;
  while (current && depth < 10) {
    const entry: LinkEntry = PAGES[current];
    trail.unshift({ path: current, label: entry.crumb ?? entry.anchor });
    current = entry.parent;
    depth += 1;
  }
  return trail;
}

/**
 * BreadcrumbList structured data built from the same trail the page renders,
 * so the visible breadcrumb and the markup can never disagree.
 */
export function breadcrumbJsonLd(path: LinkPath) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbTrail(path).map((crumb, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: crumb.label,
      item: crumb.path === "/" ? SITE.url : `${SITE.url}${crumb.path}`,
    })),
  };
}

/** Convenience for route `head()` blocks. */
export function breadcrumbMeta(path: LinkPath) {
  return {
    type: "application/ld+json",
    children: JSON.stringify(breadcrumbJsonLd(path)),
  };
}
